import { motion } from "framer-motion";
const EASE = [0.23, 1, 0.32, 1] as const;
const T = {
  x: 130,
  y: 135,
  r: 76
};
const NK = {
  x: 336,
  y: 135,
  r: 44
};
const RECEPTOR_ANGLES = [-150, -115, -80, -45, -15, 15, 45, 80, 115, 150, 180];
const ANTIBODY_ANGLES = [-45, -15, 15, 45];

/**
 * step 0: CCR4 expression · step 1: antibody binding · step 2: NK-cell mediated ADCC.
 */
export function MoaDiagram({
  step
}: {
  step: number;
}) {
  return <svg viewBox="0 0 400 270" className="block h-auto w-full" role="img" aria-label="Mechanism of action of an anti-CCR4 antibody" data-id="element-207" data-mp="3bo39hknq7m2y">
      {/* Malignant T cell */}
      <motion.circle cx={T.x} cy={T.y} r={T.r} fill="#eef1f6" stroke="#8c99aa" strokeWidth={1.2} animate={{
      strokeDasharray: step === 2 ? "5 4" : "0 0"
    }} transition={{
      duration: 0.6
    }} data-id="element-208" data-mp="3ll8z0ogoz512" />
      <path d={nucleus(T.x - 4, T.y + 2, 38)} fill="#1e3a5f" opacity={0.85} data-id="element-209" data-mp="10ugz4k5ckb0d" />
      <path d={`M${T.x - 22} ${T.y - 6} q 12 10 22 -2 q 8 -10 20 4`} fill="none" stroke="#eef1f6" strokeWidth={2} strokeLinecap="round" opacity={0.8} data-id="element-210" data-mp="15sidwsvqpo61" />

      {/* CCR4 receptors */}
      {RECEPTOR_ANGLES.map((a, i) => <g key={a} transform={`rotate(${a} ${T.x} ${T.y})`} data-id="element-211" data-mp="27jrmhepi4k0t" data-mpi={i}>
          <motion.g initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.4,
        delay: 0.3 + i * 0.04
      }} data-id="element-212" data-mp="27mdsvb0nothg">
            <line x1={T.x + T.r} y1={T.y} x2={T.x + T.r + 9} y2={T.y} stroke="#2a6f73" strokeWidth={step === 0 ? 2.4 : 1.8} data-id="element-213" data-mp="1xno89ahdkuxi" />
            <path d={`M${T.x + T.r + 13} ${T.y - 5} L${T.x + T.r + 9} ${T.y - 5} L${T.x + T.r + 9} ${T.y + 5} L${T.x + T.r + 13} ${T.y + 5}`} fill="none" stroke="#2a6f73" strokeWidth={step === 0 ? 2.4 : 1.8} strokeLinejoin="round" data-id="element-214" data-mp="1sttqiclbyq12" />
          </motion.g>
        </g>)}

      {/* Antibody bound to CCR4 */}
      {ANTIBODY_ANGLES.map((a, i) => <g key={a} transform={`rotate(${a} ${T.x} ${T.y})`} data-id="element-215" data-mp="108x1up0aafu2" data-mpi={i}>
          <motion.g initial={false} animate={{
        opacity: step >= 1 ? 1 : 0,
        x: step >= 1 ? 0 : 18
      }} transition={{
        duration: 0.6,
        delay: step >= 1 ? i * 0.08 : 0,
        ease: EASE
      }} data-id="element-216" data-mp="10bj7gj3m6l9n">
            <path d={`M${T.x + T.r + 14} ${T.y - 7} L${T.x + T.r + 26} ${T.y} L${T.x + T.r + 14} ${T.y + 7} M${T.x + T.r + 26} ${T.y} L${T.x + T.r + 44} ${T.y}`} fill="none" stroke="#1e3a5f" strokeWidth={3.2} strokeLinecap="round" strokeLinejoin="round" data-id="element-217" data-mp="v9k4397vjocp" />
            {/* defucosylated Fc tip */}
            <circle cx={T.x + T.r + 44} cy={T.y} r={2.4} fill="#ffffff" stroke="#1e3a5f" strokeWidth={1.4} data-id="element-218" data-mp="1e8ocqgvi7g3u" />
          </motion.g>
        </g>)}

      {/* NK cell */}
      <motion.g initial={false} animate={{
      opacity: step === 2 ? 1 : 0,
      x: step === 2 ? 0 : 30
    }} transition={{
      duration: 0.7,
      ease: EASE
    }} data-id="element-219" data-mp="21wfnhg1ebl30">
        <circle cx={NK.x} cy={NK.y} r={NK.r} fill="#f1f6f6" stroke="#2a6f73" strokeWidth={1.2} data-id="element-220" data-mp="3re32ujznjujy" />
        <ellipse cx={NK.x + 8} cy={NK.y} rx={20} ry={17} fill="#2a6f73" opacity={0.35} data-id="element-221" data-mp="3mfrpw0e293xi" />
        {[[-22, -16], [-26, 6], [-16, 22], [6, -28], [12, 26]].map(([dx, dy], i) => <circle key={i} cx={NK.x + dx} cy={NK.y + dy} r={2.6} fill="#2a6f73" data-id="element-222" data-mp="3ck2yxxjbxn" data-mpi={i} />)}
        {/* FcγRIIIa engagement */}
        {[-15, 15].map(a => {
        const rad = a * Math.PI / 180;
        const fx = T.x + Math.cos(rad) * (T.r + 46);
        const fy = T.y + Math.sin(rad) * (T.r + 46);
        const ang = Math.atan2(fy - NK.y, fx - NK.x);
        const nx = NK.x + Math.cos(ang) * NK.r;
        const ny = NK.y + Math.sin(ang) * NK.r;
        return <line key={a} x1={fx} y1={fy} x2={nx} y2={ny} stroke="#2a6f73" strokeWidth={2} strokeLinecap="round" data-id="element-223" data-mp="wpfuepmo1p1e" />;
      })}
      </motion.g>

      {/* Cytotoxic granules released towards the target */}
      {step === 2 && [0, 1, 2].map(i => <motion.circle key={i} r={2.2} fill="#2a6f73" initial={{
      cx: NK.x - NK.r + 4,
      cy: NK.y - 10 + i * 10,
      opacity: 0
    }} animate={{
      cx: T.x + T.r + 30,
      cy: NK.y - 12 + i * 12,
      opacity: [0, 1, 0.25]
    }} transition={{
      duration: 1.2,
      delay: 0.6 + i * 0.15,
      ease: EASE
    }} data-id="element-224" data-mp="1s0nqp80p6npr" />)}

      {/* Labels */}
      <g fontFamily="Inter, sans-serif" fontSize={11} fill="#353a42" data-id="element-225" data-mp="2azrv23hantb3">
        <text x={T.x} y={262} textAnchor="middle" data-id="element-226" data-mp="2axxawrduk582">
          Malignant T cell
        </text>
        <motion.text x={70} y={36} textAnchor="middle" animate={{
        opacity: step === 0 ? 1 : 0.55
      }} fill="#2a6f73" data-id="element-227" data-mp="2ft32o6txke42">
          CCR4
        </motion.text>
        <motion.text x={258} y={20} animate={{
        opacity: step >= 1 ? 1 : 0
      }} fill="#1e3a5f" data-id="element-228" data-mp="2uoae03bpiahc">
          Anti-CCR4 antibody (IgG1)
        </motion.text>
        <motion.text x={NK.x} y={NK.y + NK.r + 20} textAnchor="middle" animate={{
        opacity: step === 2 ? 1 : 0
      }} data-id="element-229" data-mp="j1p24i3j9cbp">
          NK cell · FcγRIIIa
        </motion.text>
        <motion.text x={300} y={80} textAnchor="middle" animate={{
        opacity: step === 2 ? 1 : 0
      }} fill="#1e3a5f" data-id="element-230" data-mp="92hrw3s3hxst">
          ADCC
        </motion.text>
      </g>
    </svg>;
}
function nucleus(cx: number, cy: number, r: number) {
  const pts: string[] = [];
  for (let k = 0; k <= 64; k++) {
    const t = k / 64 * Math.PI * 2;
    const rr = r * (1 + 0.1 * Math.sin(5 * t + 0.6) + 0.05 * Math.sin(11 * t));
    pts.push(`${(cx + Math.cos(t) * rr).toFixed(2)} ${(cy + Math.sin(t) * rr).toFixed(2)}`);
  }
  return `M${pts.join(" L")} Z`;
}